/**
 * Simple logging utility for the application
 * Provides structured logging with different levels
 */
class Logger {
  constructor() {
    // Use process.env for Jest compatibility, fallback to import.meta for Vite
    this.isDevelopment =
      typeof process !== "undefined" && process.env.NODE_ENV === "test"
        ? false
        : typeof window !== "undefined" &&
          window.location?.hostname === "localhost";
  }

  /**
   * Log info messages
   * @param {string} message - Log message
   * @param {Object} data - Additional data to log
   */
  info(message, data = {}) {
    if (this.isDevelopment) {
      console.log(`[INFO] ${message}`, data);
    }
  }

  /**
   * Log warning messages
   * @param {string} message - Log message
   * @param {Object} data - Additional data to log
   */
  warn(message, data = {}) {
    console.warn(`[WARN] ${message}`, data);
  }

  /**
   * Log error messages
   * @param {string} message - Log message
   * @param {Error|Object} error - Error object or additional data
   */
  error(message, error = {}) {
    console.error(`[ERROR] ${message}`, error);

    // In production, you might want to send errors to a logging service
    if (!this.isDevelopment && typeof error === "object" && error.stack) {
      // Could integrate with services like Sentry, LogRocket, etc.
      this.logToService(message, error);
    }
  }

  /**
   * Log email-specific events
   * @param {string} type - Email type (approval, rejection)
   * @param {string} recipient - Email recipient
   * @param {string} status - Success or failure
   * @param {Object} details - Additional details
   */
  emailEvent(type, recipient, status, details = {}) {
    const logData = {
      type,
      recipient: this.maskEmail(recipient),
      status,
      timestamp: new Date().toISOString(),
      ...details,
    };

    if (status === "success") {
      this.info(`Email sent: ${type}`, logData);
    } else {
      this.error(`Email failed: ${type}`, logData);
    }
  }

  /**
   * Mask email for privacy in logs
   * @param {string} email - Email to mask
   * @returns {string} - Masked email
   */
  maskEmail(email) {
    if (!email || typeof email !== "string") return "unknown";

    const [local, domain] = email.split("@");
    if (!domain) return "invalid-email";

    const maskedLocal =
      local.length > 2
        ? local.substring(0, 2) + "*".repeat(local.length - 2)
        : local;

    return `${maskedLocal}@${domain}`;
  }

  /**
   * Placeholder for external logging service integration
   * @param {string} message - Error message
   * @param {Error} error - Error object
   */
  logToService(message, error) {
    // Placeholder for external logging service
    // Example: Sentry.captureException(error, { extra: { message } });
  }
}

export default new Logger();
